import React from "react";

// Especially useful for Array.filter(), as this reduces the array type as well.
export function isNotNull<T extends {}>(e: T | null | undefined): e is T {
  return e !== null && e !== undefined;
}

/**
 * Type guard that checks if a value is null or undefined
 * Provide more type safety over falsy check since this will return false for 0 or ""
 * @param e - The value to check
 * @returns true if the value is null or undefined, false otherwise
 * @example
 * ```typescript
 * const values = [1, null, 'hello', undefined, 0];
 * const nullOrUndefinedValues = values.filter(isNullOrUndefined);
 * // Result: [null, undefined]
 * ```
 */
export function isNullOrUndefined<T extends {}>(
  e: T | null | undefined
): e is null | undefined {
  return e === null || e === undefined;
}

/**
 * Type guard that checks if a string value is not not null, undefined, nor empty string
 */
export function isNotEmpty(value: string | null | undefined): value is string {
  return isNotNull(value) && value.trim().length > 0;
}

// Checks to the see if a provided value is among the values of an object, and
// reduces the type accordingly. This is particularly useful for filtering enum
// values; for example `array.filter(isValueOf(SomeEnum))` will extract only the
// values matching the enum, and also reduces the type of result to SomeEnum[].
export const isValueOf =
  <T, E extends Record<keyof E, T>>(enumObject: E) =>
  (value: T): value is E[keyof E] => {
    return Object.values(enumObject).includes(value);
  };

export function isString(value: unknown): value is string {
  return typeof value === "string";
}

export function isNonEmptyString(
  value: string | null | undefined
): value is string {
  if (value === null || value === undefined) {
    return false;
  }

  if (value.trim() === "") {
    return false;
  }

  return true;
}

export function isNonEmptyNode(node: React.ReactNode): boolean {
  return React.Children.count(node) > 0;
}

export function isEmptyNode(node: React.ReactNode): boolean {
  return React.Children.count(node) <= 0;
}
